import _ from 'underscore';
import Promise from 'bluebird';
import {
  hasOwnProperty,
  defineConstProperty,
} from '@bingads-webui/reflection';
import {
  serialize,
  parse as parseRaw,
  populateIds,
  hasReadOnlyFields,
  getAttrsToSerialize,
  extractEntityErrors,
  buildDestroyObject,
} from '@bingads-webui/odata-bulk-upsert';
import type {
  CsvRow,
} from '@bingads-webui/odata-bulk-upsert';
import { odata } from '@bingads-webui/http-util';
import type {
  ODataOptions,
} from '@bingads-webui/http-util';
import { merge } from '@bingads-webui-universal/primitive-utilities';
import { join } from '@bingads-webui/url-util';
import { getSkipEntityCallPaths } from './constants';
import type {
  BulkCsvData,
  BulkEntityAttributes,
  BulkEntityId,
  BulkOperationOptions,
  BulkResourceIdentifier,
  BulkUpsertEdm,
  BulkUpsertError,
  BulkUpsertPluginHooks,
  BulkUpsertPluginOptions,
  BulkUpsertUrlOptions,
} from './types';

// Wrap the raw parse to enrich error rows with the "Error Detail" field
// that getErrorRows in odata-bulk-upsert does not capture.
function parse(csvData: BulkCsvData, isTextType?: boolean) {
  const { results, errors } = parseRaw(csvData, isTextType) as {
    results: Array<CsvRow & {
      Type?: string;
      'Error Detail'?: string;
    }>;
    errors: BulkUpsertError[];
  };

  if (!_.isEmpty(errors) && !_.isEmpty(results)) {
    // Find full error rows in results (they have Type containing ' Error')
    const fullErrorRows = _.filter(
      results,
      item => item.Type && item.Type.indexOf(' Error') > -1
    );

    // Enrich each error with ErrorDetail from the matching full row
    _.each(errors, (error: BulkUpsertError, index: number) => {
      const fullRow = fullErrorRows[index];
      if (fullRow && fullRow['Error Detail']) {
        // eslint-disable-next-line no-param-reassign
        error.ErrorDetail = fullRow['Error Detail'];
      }
    });
  }

  return { results, errors };
}

function getCast(options: BulkUpsertUrlOptions) {
  return (options && !_.isEmpty(options.cast)) ? (`/${options.cast}`) : '';
}

function getParent(options: BulkUpsertUrlOptions) {
  return (options && !_.isEmpty(options.parent)) ? (`${options.parent}/`) : '';
}

export function odataBulkUpsertPlugin(edm: BulkUpsertEdm, {
  ServiceRoot,
  AccountID,
  AdvertiserCustomerID,
  bulkUpsertSchema,
}: BulkUpsertPluginOptions = {}, {
  processOptions = _.identity,
}: BulkUpsertPluginHooks = {}) {
  if (hasOwnProperty(edm, 'odata.bulkUpsert')) {
    return;
  }

  function getUrl({
    path,
    options,
  }: {
    path: string;
    options: BulkOperationOptions;
  }) {
    if (path) {
      const prefix = path.match(/\/Customers\([0-9]+\)\/Accounts\([0-9]+\)/);
      if (!_.isEmpty(prefix)) {
        return join(ServiceRoot, `${prefix[0]}/${getParent(options)}Default.BulkUpsert${getCast(options)}`);
      }
    }
    return join(ServiceRoot, `/Customers(${AdvertiserCustomerID})/Accounts(${AccountID})/${getParent(options)}Default.BulkUpsert${getCast(options)}`);
  }

  function invalidateCache(
    entityName: string,
    ids?: Array<BulkEntityId | null>,
    skipEntityCallPaths: string[] | null = null
  ) {
    if (edm['odata.cache']) {
      edm['odata.cache'].invalidateTypeCache(entityName, null, '', skipEntityCallPaths);
      _.each(ids, (id: BulkEntityId | null) => edm['odata.cache'].invalidateTypeCache(entityName, id, '', skipEntityCallPaths));
    }
  }

  function checkParsedResult<TEntity>(
    errors: BulkUpsertError[],
    result: TEntity,
    entityName: string,
    ids: Array<BulkEntityId | null>,
    skipEntityCallPaths?: string[] | null
  ) {
    if (!_.isEmpty(errors)) {
      return Promise.reject({ // eslint-disable-line prefer-promise-reject-errors
        entity: result,
        errors,
      });
    }

    invalidateCache(entityName, ids, skipEntityCallPaths);

    return result;
  }

  _.each(bulkUpsertSchema, (bSchema, name: string) => {
    merge(edm.schema.schemas[`Model/${name}`], bSchema);
  });

  defineConstProperty(edm, 'odata.bulkUpsert', (() => {
    const {
      ResourceIdentifier,
    } = edm.resourceIdentifiers;

    ResourceIdentifier.prototype.bulkUpdate = function bulkUpdate(
      this: BulkResourceIdentifier,
      attrs: BulkEntityAttributes,
      options: BulkOperationOptions = {}
    ) {
      const { schema } = this.type;

      if (options.batch) {
        if (_.isUndefined(attrs.reqs)) {
          throw new Error('requests are required when batch bulkupserts.');
        }

        const reqs = _.map(attrs.reqs, (param) => {
          const csvRows = serialize(schema, { Id: attrs.Id, ...param.data });
          const uri = getUrl({ path: this.path, options });

          return {
            type: 'POST',
            url: uri,
            data: { CsvRows: csvRows },
          };
        });

        return odata.batch(_.defaults({ reqs, path: options.path }, options)).then(responses => (
          Promise.map(responses, (response, index: number) => {
            const { results, errors } = parse(response.data as BulkCsvData);
            const ret = populateIds(
              results,
              attrs.reqs![index].data
            ) as BulkEntityAttributes;

            return checkParsedResult(
              errors,
              ret,
              options.entityName || this.type.name,
              [attrs.Id || attrs.reqs![0].data.Id || null]
            );
          })
        ))
          .then(parsedResponse => (_.isFunction(options.processResponse)
            ? options.processResponse(parsedResponse)
            : parsedResponse));
      }

      const toSerialize = getAttrsToSerialize(attrs.Id, attrs, schema);
      const CsvRows = serialize(schema, toSerialize);

      return odata.post<ODataOptions<BulkCsvData>, BulkCsvData>(
        getUrl({ path: this.path, options }),
        processOptions({ data: { CsvRows } })
      )
        .then((res) => {
          const { results, errors } = parse(res);
          const ret = populateIds(results, attrs) as BulkEntityAttributes;

          if (results && results.length && hasReadOnlyFields(attrs, schema.properties)) {
            ret.Id = results[0].Id;
          }

          return checkParsedResult(
            errors,
            ret,
            options.entityName || this.type.name,
            [attrs.Id || null]
          );
        });
    };

    ResourceIdentifier.prototype.bulkUpdateMany = function bulkUpdateMany(
      this: BulkResourceIdentifier,
      idList: BulkEntityId[],
      attrsList: BulkEntityAttributes[],
      options: BulkOperationOptions = {}
    ) {
      const { schema } = this.type;
      const toSerialize = _.map(
        idList,
        (id: BulkEntityId, index: number) => getAttrsToSerialize(
          id,
          attrsList[index],
          schema
        )
      );
      const CsvRows = serialize(schema, toSerialize);

      return odata
        .post<ODataOptions<BulkCsvData>, BulkCsvData>(
          getUrl({ path: this.path, options }),
          _.defaults(processOptions({ data: { CsvRows } }), options)
        )
        .then((res) => {
          const { results, errors } = parse(res);
          const attrsListWithIds = populateIds(results, attrsList) as BulkEntityAttributes[];

          invalidateCache(options.entityName || this.type.name, idList);

          return _.map(attrsListWithIds, (attrs, index) => ({
            entity: attrs,
            errors: extractEntityErrors(errors, index),
          }));
        });
    };

    ResourceIdentifier.prototype.bulkCreate = function bulkCreate(
      this: BulkResourceIdentifier,
      attrs: BulkEntityAttributes,
      options: BulkOperationOptions = {}
    ) {
      const { schema } = this.type;
      const CsvRows = serialize(schema, attrs);

      return odata
        .post<ODataOptions<BulkCsvData>, BulkCsvData>(
          getUrl({ path: this.path, options }),
          _.defaults(processOptions({ data: { CsvRows } }), options)
        )
        .then((res) => {
          const { results, errors } = parse(res);
          const ret = populateIds(results, attrs) as BulkEntityAttributes;

          return checkParsedResult(errors, ret, options.entityName || this.type.name, [null]);
        });
    };

    ResourceIdentifier.prototype.bulkCreateMany = function bulkCreateMany(
      this: BulkResourceIdentifier,
      attrsList: BulkEntityAttributes[],
      options: BulkOperationOptions = {}
    ) {
      const { schema } = this.type;
      const CsvRows = serialize(schema, attrsList);

      return odata
        .post<ODataOptions<BulkCsvData>, BulkCsvData>(
          getUrl({ path: this.path, options }),
          _.defaults(processOptions({ data: { CsvRows } }), options)
        )
        .then((res) => {
          const { results, errors } = parse(res);
          const attrsListWithIds = populateIds(results, attrsList) as BulkEntityAttributes[];

          invalidateCache(options.entityName || this.type.name);

          return _.map(attrsListWithIds, (attrs, index) => ({
            entity: attrs,
            errors: extractEntityErrors(errors, index),
          }));
        });
    };

    ResourceIdentifier.prototype.bulkDestroy = function bulkDestroy(
      this: BulkResourceIdentifier,
      id: BulkEntityId,
      options: BulkOperationOptions = {}
    ) {
      const { schema } = this.type;
      const attrs = buildDestroyObject(
        schema.parent,
        id,
        options.parentId
      );
      const CsvRows = serialize(schema, attrs);

      return odata
        .post<ODataOptions<BulkCsvData>, BulkCsvData>(
          getUrl({ path: this.path, options }),
          _.defaults(processOptions({ data: { CsvRows } }), options)
        )
        .then((res) => {
          const { results, errors } = parse(res);
          const ret = populateIds(results, attrs) as BulkEntityAttributes;
          const entityName = options.entityName || this.type.name;

          return checkParsedResult(errors, ret, entityName, [id], getSkipEntityCallPaths(entityName, [id]));
        });
    };

    // put the plugin indicator
    return {};
  })());
}
